import React, { useMemo, useImperativeHandle, useRef } from 'react';
import { CSSTransition, SwitchTransition } from 'react-transition-group';

import s from './styles.module.css';

interface IProps {
  targetElementRef: React.RefObject<any>;
  transitionKey: string;
  children: React.ReactNode;
}

const classes = {
  appear: s.appear,
  appearActive: s.appearActive,
  appearDone: s.appearDone,
  enter: s.enter,
  enterActive: s.enterActive,
  enterDone: s.enterDone,
  exit: s.exit,
  exitActive: s.exitActive,
  exitDone: s.exitDone,
};

type TClasses = typeof classes;

const reversedClasses: TClasses = {
  appear: s.appear,
  appearActive: s.appearActive,
  appearDone: s.appearDone,
  enter: s.reversedEnter,
  enterActive: s.enterActive,
  enterDone: s.enterDone,
  exit: s.exit,
  exitActive: s.reversedExitActive,
  exitDone: s.exitDone,
};

interface ISwapTransitionRef {
  reverseNextTransition: () => void;
}

const SwapTransition = React.forwardRef<ISwapTransitionRef, IProps>(
  ({ targetElementRef, children, transitionKey }: IProps, ref) => {
    const remainingReverses = useRef(0);

    useImperativeHandle(ref, () => ({
      reverseNextTransition: () => {
        remainingReverses.current = 2;
      },
    }));

    const classNames = useMemo(
      () =>
        Object.defineProperties(
          {},
          Object.fromEntries(
            (Object.keys(classes) as Array<keyof TClasses>).map(classKey => [
              classKey,
              {
                get: () =>
                  remainingReverses.current > 0 ? reversedClasses[classKey] : classes[classKey],
                enumerable: true,
              },
            ]),
          ),
        ),
      [],
    );

    return (
      <SwitchTransition mode="out-in">
        <CSSTransition
          key={transitionKey}
          addEndListener={(done: any) => {
            targetElementRef.current?.addEventListener?.(
              'transitionend',
              () => {
                done();

                if (remainingReverses.current > 0) {
                  remainingReverses.current--;
                }
              },
              { once: true },
            );
          }}
          classNames={classNames}
          nodeRef={targetElementRef}
        >
          {children}
        </CSSTransition>
      </SwitchTransition>
    );
  },
);

SwapTransition.displayName = 'SwapTransition';

export type { ISwapTransitionRef };
export { SwapTransition };
